import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import AccountValidationLoading from '../../components/AccountValidationLoading';
import { Config } from '../../common/config';
import { fetchCodeDetailList } from '../../common/fetchCodeDetailList';

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 71 }, (_, idx) => currentYear - idx);
const months = Array.from({ length: 12 }, (_, idx) => idx + 1);
const days = Array.from({ length: 31 }, (_, idx) => idx + 1);

export default function AccountValidationPopup() {
  const params = new URLSearchParams(window.location.search);
  // 법인(BT003): 사업자번호로 계좌 인증 (LGD_GUBUN=5), 그 외: 예금주+생년월일 (LGD_GUBUN=3)
  const isCorporate = params.get('bizType') === 'BT003';
  const bizNo = (params.get('bizNo') || '').replace(/\D/g, '');
  const formattedBizNo =
    bizNo.length === 10
      ? `${bizNo.slice(0, 3)}-${bizNo.slice(3, 5)}-${bizNo.slice(5)}`
      : bizNo;
  const [name, setName] = useState(params.get('name') || '');
  const [year, setYear] = useState(params.get('year') || '');
  const [month, setMonth] = useState(params.get('month') || '');
  const [day, setDay] = useState(params.get('day') || '');
  const [bankCode, setBankCode] = useState(params.get('bankCode') || '');
  const [accountNo, setAccountNo] = useState(params.get('accountNo') || '');

  const [bankOptions, setBankOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const loaderTimerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const loadBanks = async () => {
      try {
        const list = await fetchCodeDetailList(
          Config.API_ADMIN_CODE_DETAIL,
          'NG11',
          url => axios.get(url, { withCredentials: true }),
        );
        if (!cancelled && list.length > 0) {
          setBankOptions(list);
        }
      } catch (e) {
        try {
          const openerBanks = window.opener?.__NAEGIFT_BANK_OPTIONS__;
          if (!cancelled && openerBanks?.length) {
            setBankOptions(openerBanks);
          }
        } catch {
          // ignore
        }
      }
    };

    loadBanks();
    return () => {
      cancelled = true;
    };
  }, []);

  const getBankNameByCode = code => {
    if (!code) return '';
    const bank = bankOptions.find(item => item.detailCode === code);
    return bank ? bank.detailCodeName : '';
  };

  const formatBankCodeForApi = code => {
    if (!code) return '';
    return code.startsWith('B0') ? code.substring(2) : code;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!isCorporate && (!year || !month || !day)) {
      setMessage('생년월일을 입력해 주세요.');
      return;
    }
    // 아래 값이 비거나 형식이 틀리면 서버 검증에서 영어 오류가 뜨므로 먼저 안내한다.
    if (!bankCode) {
      setMessage('은행을 선택해 주세요.');
      return;
    }
    if (!accountNo) {
      setMessage('계좌번호를 입력해 주세요.');
      return;
    }
    if (accountNo.length > 20) {
      setMessage('계좌번호는 20자 이하로 입력해 주세요.');
      return;
    }
    if (!isCorporate && !name.trim()) {
      setMessage('예금주명을 입력해 주세요.');
      return;
    }
    if (isCorporate && bizNo.length !== 10) {
      setMessage('사업자등록번호 10자리를 확인해 주세요.');
      return;
    }
    setIsLoading(true);
    setStatus('loading');
    setMessage('');
    const startTime = Date.now();

    const birth =
      year && month && day
        ? `${String(year).slice(-2)}${String(month).padStart(2, '0')}${String(
            day
          ).padStart(2, '0')}`
        : '';
    try {
      const response = await axios.post(
        `${Config.API_SERVER}account-validation`,
        {
          bankCode: formatBankCodeForApi(bankCode),
          accountNo: accountNo,
          ...(!isCorporate && { accountHolder: name }),
          privateNo: isCorporate ? bizNo : birth,
          ...(isCorporate && { bizType: 'BT003' }),
        },
        { withCredentials: true }
      );
      const result = response?.data?.data?.data;
      if (response?.data?.resultCode === 200 && result?.isValid) {
        setStatus('success');
        setMessage('계좌 유효성 검증이 완료되었습니다.');
      } else {
        setStatus('error');
        setMessage(result?.responseMessage || '계좌 유효성 검증에 실패했습니다.');
      }
    } catch (error) {
      setStatus('error');
      const serverMsg =
        error.response?.data?.message ||
        error.response?.data?.resultMessage ||
        error.message;
      setMessage(
        serverMsg && serverMsg !== 'Network Error'
          ? serverMsg
          : '계좌 유효성 검증 중 오류가 발생했습니다.'
      );
    } finally {
      const elapsed = Date.now() - startTime;
      const MIN_MS = 4000;
      const delay = Math.max(0, MIN_MS - elapsed);
      if (loaderTimerRef.current) {
        clearTimeout(loaderTimerRef.current);
      }
      loaderTimerRef.current = setTimeout(() => {
        setIsLoading(false);
      }, delay);
    }
  };

  const handleConfirmClose = () => {
    if (window.opener) {
      window.opener.postMessage(
        status === 'success'
          ? {
              type: 'ACCOUNT_VALIDATION_RESULT',
              success: true,
              data: {
                bankCode,
                accountNo,
                accountHolder: isCorporate ? '' : name,
                bizType: isCorporate ? 'BT003' : 'BT002',
              },
            }
          : {
              type: 'ACCOUNT_VALIDATION_RESULT',
              success: false,
              errorMessage: message || '계좌 유효성 검증 실패',
            },
        '*'
      );
    }
    window.close();
  };

  const ResultView = ({ success }) => (
    <div
      style={{
        width: 360,
        maxWidth: '90vw',
        background: '#ffffff',
        borderRadius: 16,
        boxShadow: '0 12px 36px rgba(0,0,0,0.12)',
        overflow: 'hidden',
        padding: '32px 24px',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          margin: '0 auto 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: success
            ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
            : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          boxShadow: success
            ? '0 4px 20px rgba(34, 197, 94, 0.4)'
            : '0 4px 20px rgba(239, 68, 68, 0.4)',
        }}
      >
        <span
          style={{
            fontSize: 44,
            color: '#ffffff',
            lineHeight: 1,
            fontWeight: 700,
          }}
        >
          {success ? '✓' : '✕'}
        </span>
      </div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 600,
          color: '#111827',
          lineHeight: 1.5,
          marginBottom: 28,
        }}
      >
        {success
          ? '계좌가 안전하게 등록되었습니다.'
          : message || '계좌 정보가 일치하지 않습니다.'}
      </div>
      <button
        type="button"
        onClick={handleConfirmClose}
        style={{
          width: '100%',
          padding: '14px 24px',
          fontSize: 16,
          fontWeight: 600,
          color: '#ffffff',
          border: 'none',
          borderRadius: 12,
          cursor: 'pointer',
          background: success
            ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
            : '#6b7280',
        }}
      >
        확인
      </button>
    </div>
  );

  useEffect(() => {
    return () => {
      if (loaderTimerRef.current) {
        clearTimeout(loaderTimerRef.current);
      }
    };
  }, []);

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#f5f7fb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <AccountValidationLoading
        open={isLoading}
        name={name}
        birthY={year}
        birthM={month}
        birthD={day}
        bizNo={isCorporate ? formattedBizNo : ''}
        bankName={getBankNameByCode(bankCode)}
        accountNo={accountNo}
      />
      {!isLoading && (status === 'success' || status === 'error') ? (
        <ResultView success={status === 'success'} />
      ) : (
        <div
          style={{
            width: 360,
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 12px 36px rgba(0,0,0,0.12)',
            padding: 24,
          }}
        >
          <div style={{ marginBottom: 8 }}>
            <div
              style={{
                fontWeight: 700,
                fontSize: 18,
                color: '#111827',
                lineHeight: 1.5,
              }}
            >
              {isCorporate
                ? '법인 계좌 인증을 진행해 주세요.'
                : '은행 계좌 인증을 진행해 주세요.'}
            </div>
            <div
              style={{
                marginTop: 4,
                fontSize: 12,
                color: '#6b7280',
                lineHeight: 1.6,
              }}
            >
              {isCorporate
                ? '법인 명의 계좌와 조회한 사업자등록번호가'
                : '회원님의 실명과 계좌에 등록된 이름이 일치해야'}
            </div>
            <div
              style={{
                marginTop: 4,
                fontSize: 12,
                color: '#6b7280',
                lineHeight: 1.6,
              }}
            >
              {isCorporate ? '일치해야 인증이 가능합니다.' : '인증이 가능합니다.'}
            </div>
          </div>
          <form onSubmit={handleSubmit}>
            {isCorporate ? (
              <div style={{ marginBottom: 12 }}>
                <label
                  style={{ display: 'block', fontSize: 12, color: '#666' }}
                >
                  사업자등록번호
                </label>
                <input
                  className="input_01"
                  type="text"
                  value={formattedBizNo}
                  readOnly
                  disabled
                  style={{ width: '100%' }}
                />
              </div>
            ) : (
              <>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 12, color: '#666' }}>
                    이름
                  </label>
                  <input
                    className="input_01"
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    required
                    style={{ width: '100%' }}
                  />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 12, color: '#666' }}>
                    생년월일
                  </label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <select
                      className="input_01"
                      value={year}
                      onChange={e => setYear(e.target.value)}
                      required
                    >
                      <option value="">연도</option>
                      {years.map(y => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                    <select
                      className="input_01"
                      value={month}
                      onChange={e => setMonth(e.target.value)}
                      required
                    >
                      <option value="">월</option>
                      {months.map(m => (
                        <option key={m} value={String(m).padStart(2, '0')}>
                          {String(m).padStart(2, '0')}
                        </option>
                      ))}
                    </select>
                    <select
                      className="input_01"
                      value={day}
                      onChange={e => setDay(e.target.value)}
                      required
                    >
                      <option value="">일</option>
                      {days.map(d => (
                        <option key={d} value={String(d).padStart(2, '0')}>
                          {String(d).padStart(2, '0')}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </>
            )}
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#666' }}>
                은행 (선택)
              </label>
              <select
                className="input_01"
                value={bankCode}
                onChange={e => setBankCode(e.target.value)}
                style={{ width: '100%' }}
              >
                <option value="">선택 안함</option>
                {bankOptions.map((bank, index) => (
                  <option key={index} value={bank.detailCode}>
                    {bank.detailCodeName}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#666' }}>
                계좌번호
              </label>
              <input
                className="input_01"
                type="text"
                maxLength={20}
                value={accountNo}
                onChange={e =>
                  setAccountNo(e.target.value.replace(/[^0-9]/g, ''))
                }
                required
                style={{ width: '100%' }}
              />
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 10,
                color: status === 'error' ? '#d32f2f' : '#4a5568',
                minHeight: 20,
              }}
            >
              {message}
            </div>

            <div
              style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}
            >
              <button
                type="button"
                className="button_05 button_gray"
                onClick={() => window.close()}
              >
                닫기
              </button>
              <button
                type="submit"
                className="button_05 button_blue"
                disabled={isLoading}
              >
                {isLoading ? '검증 중...' : '확인'}
              </button>
            </div>
          </form>
        </div>
      )}

      <style>
        {`
          @keyframes ng-spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}
